import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';
// Sprint 140 — `Statement` and `dateUtils` were imported solely for the deleted
// client-side generator; both go with it.
import { nextRequestToken, isStaleRequest, loadStart, loadSettle } from './shared';

export const createStatementsPaymentsSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // Statement Management with API integration
  fetchStatements: async (condoId?: string) => {
    const token = nextRequestToken('statements');
    set(loadStart('statements')); // 106.1
    try {
      const statements = await apiService.getStatements(condoId);
      if (isStaleRequest(token)) return; // 94.4 — superseded by a newer fetch
      set(state => ({ ...loadSettle('statements', null)(state), statements }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      set(loadSettle('statements', error.message || 'Failed to fetch statements'));
    }
  },

  // Sprint 140 (R13-1, ADR-135) — `generateStatements` was REMOVED here.
  //
  // It was the pre-endpoint implementation: before StatementController existed,
  // this page computed statements in the browser. Statements are DERIVED from
  // fees server-side and there is no statements table, so there was never
  // anything for a client 'generate' to create — `GET /api/statements` already
  // returns every statement that exists.
  //
  // Left in place it did real harm: it appended a DUPLICATE row per owner (its
  // dedup guard compared the numeric owners.id against the backend's user UUID,
  // so it never matched) carrying `totalFees - totalPayments` over NON-PAID fees
  // — the exact arithmetic ADR-129 fixed server-side, understating what an owner
  // owed. It also counted WAIVED fees as owed (harmless until Sprint 139's V44
  // made WAIVED persistable) and ignored each fee's period.
  //
  // The Statements page is now server-derived end to end; see ADR-135.

  sendStatementEmail: async (statementId) => {
    const { statements, owners } = get();
    const statement = statements.find(s => s.id === statementId);

    if (!statement) throw new Error('Statement not found');

    const owner = owners.find(o => o.id === statement.ownerId);
    if (!owner) throw new Error('Owner not found');

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      console.log(`Sending statement to ${owner.email} for period ${statement.period}`);

      set(state => ({
        statements: state.statements.map(s =>
          s.id === statementId ? { ...s, isSent: true } : s
        )
      }));

      return Promise.resolve();
    } catch (error) {
      console.error('Failed to send email:', error);
      throw new Error('Failed to send email');
    }
  },

  // Sprint 146 (R14-13) — `period === null` means "every unsent statement",
  // which is what the Statements page's new default "All" filter mode passes.
  // Without it, Send All would match no statement's period and silently report
  // "Sent 0 of 0" — a button that does nothing while claiming success.
  sendBulkStatements: async (period: string | null, condoId?: string) => {
    const { statements } = get();
    const inPeriod = (s: any) => period === null || s.period === period;
    const filteredStatements = condoId ?
      statements.filter(s => s.condoId === condoId && inPeriod(s) && !s.isSent) :
      statements.filter(s => inPeriod(s) && !s.isSent);

    const results: { statementId: string; success: boolean; error?: string }[] = [];

    for (const statement of filteredStatements) {
      try {
        await get().sendStatementEmail(statement.id);
        results.push({ statementId: statement.id, success: true });
      } catch (error) {
        results.push({
          statementId: statement.id,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    return results;
  },

  getOwnerStatements: (ownerId) =>
    get().statements
      .filter(s => s.ownerId === ownerId)
      .sort((a, b) => new Date(b.generatedDate).getTime() - new Date(a.generatedDate).getTime()),

  getStatementById: (id) =>
    get().statements.find(s => s.id === id),

  // Payment Processing with API integration
  fetchPayments: async (condoId?: string) => {
    const token = nextRequestToken('payments');
    set(loadStart('payments')); // 106.1
    try {
      const payments = await apiService.getPayments(condoId);
      if (isStaleRequest(token)) return; // 94.4 — superseded by a newer fetch
      set(state => ({ ...loadSettle('payments', null)(state), payments }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      set(loadSettle('payments', error.message || 'Failed to fetch payments'));
    }
  },

  // Sprint 108 (ADR-101) — `processPayment` (the fabricated `pay-${Date.now()}`
  // client mock) is deleted. Settlement now persists server-side (markFeeAsPaidApi
  // for the demo path, Stripe form for card/ACH) and the ledger is read back via
  // fetchPayments; the store never invents payment rows.

  createPayment: async (paymentData) => {
    const { setLoading, setError, fetchPayments } = get();
    setLoading(true);
    try {
      await apiService.createPayment(paymentData);
      await fetchPayments(paymentData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create payment');
    } finally {
      setLoading(false);
    }
  },

  getOwnerPayments: (ownerId) =>
    get().payments
      .filter(p => p.ownerId === ownerId)
      .sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()),

  getPaymentById: (id) =>
    get().payments.find(p => p.id === id),

  getRecentPayments: (limit = 10, condoId?: string) => {
    const { payments } = get();
    const filteredPayments = condoId ? payments.filter(p => p.condoId === condoId) : payments;
    return filteredPayments
      .sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime())
      .slice(0, limit);
  },

  getPaymentsByUnit: (unitId) => {
    const { payments, units } = get();
    const unit = units.find(u => u.id === unitId);
    if (unit?.ownerId) {
      return payments
        .filter(p => p.ownerId === unit.ownerId)
        .sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());
    }
    return [];
  },
});
